/**
 * Display units — SI (the app's native units) or US customary.
 *
 * DISPLAY ONLY. Every value in state, localStorage, the saved project JSON and
 * every API payload stays in SI exactly as before. This module converts on the
 * way to the screen / PDF, and converts a US entry back to SI the moment it is
 * typed, so validators, autocorrect and the backend only ever see SI.
 *
 * The backend mirror for the Throughput Schedule PDF is backend/units.py —
 * keep the factors and rounding rules in step.
 */

export type UnitSystem = "SI" | "US";

export type Quantity =
  | "temp"        // °C → °F (absolute)
  | "tempDelta"   // K  → °F (difference: ×1.8, no offset)
  | "density"     // kg/m³ → lb/ft³
  | "length"      // mm → in, nearest ⅛
  | "lengthFine"  // mm → in, 3 dp (orifice — ⅛" is far too coarse)
  | "mass"        // kg → lb (block / station / batch weights)
  | "throughput"  // kg per period → lb (per day, per N cycles)
  | "tonnes"      // t (1000 kg) → US short ton (2000 lb) — Gantt t/shift
  | "capacity"    // kg/hr product throughput → lb/hr
  | "massFlow"    // kg/hr refrigerant / fluid flow → lb/hr
  | "power"       // kW → BTU/hr
  | "volFlow"     // L/hr → US gal/hr
  | "volume"      // L → US gal
  | "pressure";   // bar → psi

// ── Conversion factors (exact definitions where they exist) ─────────────────
const MM_PER_IN = 25.4;
const KG_PER_LB = 0.45359237;
const KG_PER_USTON = 2000 * KG_PER_LB;
const KGM3_PER_LBFT3 = 16.018463;
const W_PER_BTUHR = 0.29307107;
const L_PER_USGAL = 3.785411784;
const BAR_PER_PSI = 0.0689475729;

interface QuantitySpec {
  si: string;
  us: string;
  toUS: (si: number) => number;
  toSI: (us: number) => number;
  /** US display string for a US-unit number. */
  formatUS: (us: number) => string;
}

// ── Formatting helpers ──────────────────────────────────────────────────────

const MINUS = "−";

/** Fixed decimals with thousands separators; never prints "−0.0". */
function fixed(x: number, decimals: number): string {
  const f = 10 ** decimals;
  let r = Math.round(x * f) / f;
  if (Object.is(r, -0) || r === 0) r = 0;
  return r.toLocaleString("en-GB", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Round to the nearest `step` and print as an integer with separators. */
function stepped(x: number, step: number): string {
  return fixed(Math.round(x / step) * step, 0);
}

const EIGHTHS = ["", "⅛", "¼", "⅜", "½", "⅝", "¾", "⅞"]; // ⅛ ¼ ⅜ ½ ⅝ ¾ ⅞

/** Inches to the nearest ⅛ using precomposed vulgar-fraction glyphs: 31⅞, ⅝, 3. */
export function fmtEighths(inches: number): string {
  const n = Math.round(Math.abs(inches) * 8);
  if (n === 0) return "0";
  const whole = Math.floor(n / 8);
  const frac = EIGHTHS[n % 8];
  const sign = inches < 0 ? MINUS : "";
  return `${sign}${whole ? fixed(whole, 0) : ""}${frac}`;
}

/** Nearest ½ (½ glyph), e.g. 52½. Used for small masses. */
function fmtHalves(x: number): string {
  const n = Math.round(Math.abs(x) * 2);
  const whole = Math.floor(n / 2);
  const sign = x < 0 && n ? MINUS : "";
  if (n % 2 === 0) return `${sign}${fixed(whole, 0)}`;
  return `${sign}${whole ? fixed(whole, 0) : ""}½`;
}

/**
 * Mass rounding policy (lb). Precision follows magnitude so a figure never
 * claims more accuracy than the estimate behind it:
 *   < 100 lb     nearest ½ lb   (block weights, e.g. 52½)
 *   ≥ 100 lb     whole lb       (station and batch weights)
 */
function fmtMassLb(lb: number): string {
  return Math.abs(lb) < 100 ? fmtHalves(lb) : fixed(lb, 0);
}

/** Production throughput (lb, lb/hr): thousands round to the nearest 50. */
function fmtThroughputLb(lb: number): string {
  return Math.abs(lb) >= 1000 ? stepped(lb, 50) : fixed(lb, 0);
}

export const QUANTITIES: Record<Quantity, QuantitySpec> = {
  temp: {
    si: "°C", us: "°F",
    toUS: (c) => c * 1.8 + 32, toSI: (f) => (f - 32) / 1.8,
    formatUS: (f) => fixed(f, 1),
  },
  tempDelta: {
    si: "K", us: "°F",
    toUS: (k) => k * 1.8, toSI: (f) => f / 1.8,
    formatUS: (f) => fixed(f, 1),
  },
  density: {
    si: "kg/m³", us: "lb/ft³",
    toUS: (x) => x / KGM3_PER_LBFT3, toSI: (x) => x * KGM3_PER_LBFT3,
    formatUS: (x) => fixed(x, 1),
  },
  length: {
    si: "mm", us: "in",
    toUS: (mm) => mm / MM_PER_IN, toSI: (inch) => inch * MM_PER_IN,
    formatUS: fmtEighths,
  },
  lengthFine: {
    si: "mm", us: "in",
    toUS: (mm) => mm / MM_PER_IN, toSI: (inch) => inch * MM_PER_IN,
    formatUS: (inch) => fixed(inch, 3),
  },
  mass: {
    si: "kg", us: "lb",
    toUS: (kg) => kg / KG_PER_LB, toSI: (lb) => lb * KG_PER_LB,
    formatUS: fmtMassLb,
  },
  throughput: {
    si: "kg", us: "lb",
    toUS: (kg) => kg / KG_PER_LB, toSI: (lb) => lb * KG_PER_LB,
    formatUS: fmtThroughputLb,
  },
  tonnes: {
    si: "t", us: "US ton",
    toUS: (t) => (t * 1000) / KG_PER_USTON, toSI: (ton) => (ton * KG_PER_USTON) / 1000,
    formatUS: (ton) => fixed(ton, 1),
  },
  capacity: {
    si: "kg/hr", us: "lb/hr",
    toUS: (kg) => kg / KG_PER_LB, toSI: (lb) => lb * KG_PER_LB,
    formatUS: fmtThroughputLb,
  },
  massFlow: {
    si: "kg/hr", us: "lb/hr",
    toUS: (kg) => kg / KG_PER_LB, toSI: (lb) => lb * KG_PER_LB,
    formatUS: (lb) => fixed(lb, 0),
  },
  power: {
    si: "kW", us: "BTU/hr",
    // 0.1 kW ≈ 341 BTU/hr, so the nearest 100 BTU/hr matches the SI precision
    toUS: (kw) => (kw * 1000) / W_PER_BTUHR, toSI: (btu) => (btu * W_PER_BTUHR) / 1000,
    formatUS: (btu) => stepped(btu, 100),
  },
  volFlow: {
    si: "L/hr", us: "gal/hr",
    toUS: (l) => l / L_PER_USGAL, toSI: (g) => g * L_PER_USGAL,
    formatUS: (g) => fixed(g, 0),
  },
  volume: {
    si: "L", us: "gal",
    toUS: (l) => l / L_PER_USGAL, toSI: (g) => g * L_PER_USGAL,
    formatUS: (g) => fixed(g, 0),
  },
  pressure: {
    si: "bar", us: "psi",
    toUS: (b) => b / BAR_PER_PSI, toSI: (p) => p * BAR_PER_PSI,
    formatUS: (p) => fixed(p, 2),
  },
};

// ── Public helpers ──────────────────────────────────────────────────────────

/** Unit label for a quantity in the given system. */
export function unitLabel(q: Quantity, system: UnitSystem): string {
  return system === "US" ? QUANTITIES[q].us : QUANTITIES[q].si;
}

const DASH = "—";

/**
 * Format an SI value for display. In SI the caller's own formatter is used
 * unchanged (so the SI screen stays byte-for-byte as it was); in US the value
 * is converted and rounded per QUANTITIES.
 */
export function formatQty(
  v: number | null | undefined,
  q: Quantity,
  system: UnitSystem,
  siFormat: (v: number) => string,
): string {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  if (system === "SI") return siFormat(v);
  const spec = QUANTITIES[q];
  return spec.formatUS(spec.toUS(v));
}

/** "800 x 600 x 76" (mm) → "31½ × 23⅝ × 3" (in). Non-numeric parts pass through. */
export function formatBlockSize(s: string | null | undefined, system: UnitSystem): string {
  if (!s) return DASH;
  if (system === "SI") return s;
  return s.split(/\s*[x×]\s*/i)
    .map((p) => (p.trim() !== "" && isFinite(Number(p)) ? fmtEighths(Number(p) / MM_PER_IN) : p))
    .join(" × ");
}

// ── Editable fields: SI string ⇄ US string ──────────────────────────────────

const GLYPH_VALUE: Record<string, number> = {
  "⅛": 1 / 8, "¼": 1 / 4, "⅜": 3 / 8, "½": 1 / 2,
  "⅝": 5 / 8, "¾": 3 / 4, "⅞": 7 / 8,
};

/**
 * Parse a typed US number. Accepts decimals and inch fractions:
 * "31.875", "31 7/8", "31-7/8", "31⅞", "7/8", "⅞", "−4". NaN if unparseable.
 */
export function parseUSNumber(raw: string): number {
  let s = raw.trim().replace(/−/g, "-");
  if (s === "") return NaN;
  let neg = false;
  if (s.startsWith("-")) { neg = true; s = s.slice(1).trim(); }
  let value: number;
  const glyph = /^(\d*)\s*([⅛¼⅜½⅝¾⅞])$/.exec(s);
  const mixed = /^(?:(\d+)[\s-]+)?(\d+)\/(\d+)$/.exec(s);
  if (glyph) {
    value = (glyph[1] ? Number(glyph[1]) : 0) + GLYPH_VALUE[glyph[2]];
  } else if (mixed) {
    const den = Number(mixed[3]);
    if (den === 0) return NaN;
    value = (mixed[1] ? Number(mixed[1]) : 0) + Number(mixed[2]) / den;
  } else if (/^(\d+\.?\d*|\.\d+)$/.test(s)) {
    value = Number(s);
  } else {
    return NaN;
  }
  return neg ? -value : value;
}

/** Plain SI number for storage: 2 dp, trailing zeros trimmed ("809.63", "-17.78", "810"). */
function siString(x: number): string {
  const r = Math.round(x * 100) / 100;
  return String(Object.is(r, -0) ? 0 : r);
}

/** Compact US entry string: the quantity's display format without separators. */
function usInputString(us: number, q: Quantity): string {
  const s = QUANTITIES[q].formatUS(us).replace(/,/g, "");
  // Inputs read better as "-40" than "−40.0": ASCII minus, no trailing ".0"
  return s.replace(MINUS, "-").replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
}

/**
 * Stored SI field string → what the US-mode input shows. The sticky "!"
 * override marker (lib/autocorrect) is carried across. Blank and non-numeric
 * strings pass through untouched so their error messages still make sense.
 */
export function siInputToUS(value: string, q: Quantity): string {
  const override = value.includes("!");
  const clean = value.replace(/!/g, "").trim();
  if (clean === "" || Number.isNaN(Number(clean))) return value;
  const s = usInputString(QUANTITIES[q].toUS(Number(clean)), q);
  return override ? `${s}!` : s;
}

/** Typed US string → SI string to store. Unparseable input passes through raw. */
export function usInputToSI(value: string, q: Quantity): string {
  const override = value.includes("!");
  const clean = value.replace(/!/g, "").trim();
  if (clean === "") return override ? "!" : "";
  const n = parseUSNumber(clean);
  if (Number.isNaN(n)) return value;
  const s = siString(QUANTITIES[q].toSI(n));
  return override ? `${s}!` : s;
}

// ── Messages ────────────────────────────────────────────────────────────────

const NUM = String.raw`[−-]?\d+(?:\.\d+)?`;
const toNumber = (s: string) => Number(s.replace(MINUS, "-"));
/** Unit tags that appear in validator messages, mapped to their quantity. */
const MSG_UNITS: [string, Quantity][] = [
  ["kg/m³", "density"], ["°C", "temp"], ["mm", "length"], ["K", "tempDelta"], ["kg/h", "capacity"],
];

function usMsgNumber(si: number, q: Quantity): string {
  return QUANTITIES[q].formatUS(QUANTITIES[q].toUS(si));
}

/**
 * Rewrite a validator / hard-limit message for US display. Every number that
 * carries a unit tag is converted ("Must be 2–45 °C" → "Must be 35.6–113.0 °F",
 * "(10 K above outlet)" → "(18.0 °F above outlet)"). Untagged numbers are left
 * alone, so a message without units is returned unchanged.
 */
export function localiseMessage(msg: string | undefined, system: UnitSystem): string | undefined {
  if (!msg || system === "SI") return msg;
  let out = msg;
  for (const [tag, q] of MSG_UNITS) {
    const esc = tag.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
    const us = QUANTITIES[q].us;
    // ranges first: "2–45 °C", "−25 to −10 °C"
    out = out.replace(new RegExp(`(${NUM})(\\s*(?:–| to )\\s*)(${NUM})\\s*${esc}(?![\\w/³])`, "g"),
      (_m, a, sep, b) => `${usMsgNumber(toNumber(a), q)}${sep}${usMsgNumber(toNumber(b), q)} ${us}`);
    out = out.replace(new RegExp(`(${NUM})\\s*${esc}(?![\\w/³])`, "g"),
      (_m, a) => `${usMsgNumber(toNumber(a), q)} ${us}`);
  }
  return out;
}

/**
 * Rewrite an autocorrect note ("815 → 820 (standard size)") for US display.
 * Every number in a field's note is that field's own quantity, so all of them
 * convert. Returns undefined when the correction is invisible in US units
 * (809.63 mm → 810 mm both read 31⅞ in).
 */
export function localiseNote(note: string | undefined, q: Quantity | undefined, system: UnitSystem): string | undefined {
  if (!note || !q || system === "SI") return note;
  const nums: string[] = [];
  const out = note.replace(new RegExp(NUM, "g"), (m) => {
    const s = usInputString(QUANTITIES[q].toUS(toNumber(m)), q);
    nums.push(s);
    return s;
  });
  if (nums.length >= 2 && nums[0] === nums[1]) return undefined;
  return out;
}
